import React, { useState, useEffect, useRef } from 'react';
import { Collapse, Steps, Tooltip } from 'antd';
import { DownOutlined, RightOutlined, LeftOutlined, CheckCircleFilled, ClockCircleFilled, LoadingOutlined } from '@ant-design/icons';

const { Panel } = Collapse;

/**
 * StepsComponent for displaying a process flow with steps
 * 
 * @param {Object} props
 * @param {Object} props.data - The steps data as an object with step name and status
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 */
const StepsComponent = ({
  data,
  title = "Process Steps",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent
}) => {
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };

  //////////  Component data sync from backend ?????????????????
  const [updateCounter, setUpdateCounter] = useState(0);

  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      // Create a refresh function to update this component
      const refreshComponent = () => {
        console.log(`Refreshing Component: ${uuid}`);
        setUpdateCounter(prev => prev + 1);
      };
      
      // Register this component with the provided function
      const unregister = registerComponent(refreshComponent);
      
      // Unregister when the component unmounts
      return unregister;
    }
  }, [registerComponent, uuid]);
  //////////  Component data sync from backend ?????????????????



  // Map status to Ant Design Steps status
  const mapStatusToStepStatus = (status) => {
    switch(status) {
      case 'Completed': return 'finish';
      case 'In-Progress': return 'process';
      case 'Waiting': return 'wait';
      case 'Error': return 'error';
      default: return 'wait';
    }
  };

  // Map status to icon
  const getStepIcon = (status) => {
    switch(status) {
      case 'Completed': return <CheckCircleFilled />;
      case 'In-Progress': return <LoadingOutlined />;
      case 'Waiting': return <ClockCircleFilled />;
      default: return null;
    }
  };

  // Convert data to items array for Steps component
  const getStepsItems = () => {
    if (!data || !data.steps || !Array.isArray(data.steps)) return [];
    
    return data.steps.map((step, index) => ({
      title: (
        <span style={{ 
          color: step.status === 'Completed' ? '#1890ff' : 
                 step.status === 'In-Progress' ? '#000000' : 
                 '#8c8c8c',
          fontWeight: step.status === 'In-Progress' ? 'bold' : 'normal'
        }}>
          {step.title}
        </span>
      ),
      status: mapStatusToStepStatus(step.status),
      description: (
        <div style={{ 
          color: '#8c8c8c',
          paddingLeft: '0px'
        }}>
          {step.description}
        </div>
      )
    }));
  };
  
  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;
  
  return (
    <div style={{ 
      marginBottom: '16px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          setIsExpanded(isActive);
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        items={[
          {
            key: '1',
            label: (
              <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                {title}
              </div>
            ),
            children: (
              <div>
                {/* Steps Component */}
                <div style={{ padding: '16px 0' }}>
                  <Steps 
                    direction="vertical"
                    items={getStepsItems()}
                    size="default"
                    progressDot={true}
                    current={(data && data.steps ? data.steps.findIndex(step => step.status === 'In-Progress') : 0)}
                  />
                </div>
                
                {/* Version navigation */}
                {showVersionNavigation && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <button 
                      size="small" 
                      icon={<LeftOutlined />}
                      disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex > 0) {
                          handleVersionChange(versions[currentIndex - 1]);
                        }
                      }}
                    >
                      Previous
                    </button>
                    
                    <div>
                      Version {currentVersion} of {versionInfo.total_versions}
                    </div>
                    
                    <button 
                      size="small" 
                      icon={<RightOutlined />}
                      disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex < versions.length - 1) {
                          handleVersionChange(versions[currentIndex + 1]);
                        }
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default StepsComponent;